import React, { useEffect, useState } from 'react';
import { Input, Button, Spinner } from 'reactstrap';
import {
  createTask,
  getTeammember,
  getUserProfile,
  TaskCreateDto
} from '../../Api/ApiUrlConst';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onTaskCreated: () => void;
  initialProjectId?: string;
  projectEndDate: string | null;
}

interface TeamMember {
  userID: string;
  name: string;
  department: string;
}

interface CurrentUser {
  role: string;
  department: string;
}

interface SubTaskFormData {
  description: string;
  assignedToId: string;
  dueDate: string;
  status: string;
  priority: string;
  workHours: number;
  completionDate?: string;
  remarks: string;
  issues: string;
}

interface TaskFormData {
  description: string;
  projectId: number;
  assignedToId: string;
  dueDate?: string | null ;
  status: string;
  priority: string;
  percentCompleted: number;
  remarks: string;
  completionDate?: string | null;
  issues: string;
  subTasks: SubTaskFormData[];
}
const statusOptions = [
  { value: "YetToStart", label: "Yet to Start" },
  { value: "WaitingForInput", label: "Waiting for Input" },
  { value: "", label: "" },
  { value: "ModellingTodo", label: "Modelling ToDo" },
  { value: "ModellingInProgress", label: "Modelling in Progress" },
  { value: "ModellingCompleted", label: "Modelling Completed" },
  { value: "", label: "" },
  { value: "DraftingTodo", label: "Drafting ToDo" },
  { value: "DraftingInProgress", label: "Drafting in Progress" },
  { value: "DraftingCompleted", label: "Drafting Completed" },
  { value: "", label: "" },
  { value: "QCTodo", label: "QC ToDo" },
  { value: "QCInProgress", label: "QC in Progress" },
  { value: "QCCompleted", label: "QC Completed" },
  { value: "", label: "" },
  { value: "ClientReviewTodo", label: "Client Review ToDo" },
  { value: "ClientReviewInProgress", label: "Client Review in Progress" },
  { value: "ClientReviewCompleted", label: "Client Review Completed" },
  { value: "", label: "" },
  { value: "FinalCorrectionTodo", label: "Final Correction ToDo" },
  { value: "FinalCorrectionInProgress", label: "Final Correction in Progress" },
  { value: "FinalCorrectionCompleted", label: "Final Correction Completed" },
  { value: "", label: "" },
  { value: "RevisionTodo", label: "Revision ToDo" },
  { value: "RevisionInProgress", label: "Revision in Progress" },
  { value: "RevisionCompleted", label: "Revision Completed" },
  { value: "", label: "" },
  { value: "Cancelled", label: "Cancelled" },
  { value: 'Issues', label: 'Issues' },
  { value: "OnHold", label: "On Hold" },
  { value: "Released", label: "Released" },
  { value: "NotInScope", label: "Not In Scope" },
  { value: "Completed", label: "Ready for Release" },
];


const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];

const AddTaskSubTaskComponents: React.FC<Props> = ({
  isOpen,
  toggle,
  onTaskCreated,
  initialProjectId = '',
  projectEndDate
}) => {
  const { department } = useAuth(); // ✅ From logged-in user
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<TaskFormData>({
    description: '',
    projectId: initialProjectId ? parseInt(initialProjectId) : 0,
    assignedToId: '',
    dueDate: null,
    status: 'YetToStart',
    priority: 'Medium',
    percentCompleted: 0,
    completionDate: null,
    remarks: '',
    issues: '',
    subTasks: [],
  });

  useEffect(() => {
    if (!isOpen) return;

    setError(null);
    setFormData(prev => ({
      ...prev,
      description: '',
      assignedToId: '',
      dueDate: null,
      status: 'YetToStart',
      priority: 'Medium',
      percentCompleted: 0,
      completionDate: null,
      remarks: '',
      issues: '',
      subTasks: [],
      projectId: initialProjectId ? parseInt(initialProjectId) : 0,
    }));

    const fetchData = async () => {
      try {
        setLoadingMembers(true);

        // Get profile (optional, if you want more than just department from useAuth)
        const userRes = await getUserProfile();
        setCurrentUser(userRes.data);

        // Get all members
        const tmRes = await getTeammember();
        const membersData = tmRes.data?.$values || tmRes.data || [];
        setTeamMembers(membersData);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchData();
  }, [isOpen, initialProjectId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => {
      let newValue: any = value;
      if (name === 'percentCompleted' || name === 'projectId') {
        newValue = Number(value);
      }
      return { ...prev, [name]: newValue };
    });
  };

  const validateForm = (): string | null => {
    if (!formData.projectId || formData.projectId === 0)
      return 'Please select a valid project.';
    if (!formData.description.trim())
      return 'Task description is required.';
    if (!formData.assignedToId)
      return 'Task assignee is required.';
    if (formData.dueDate && projectEndDate) {
      if (new Date(formData.dueDate) > new Date(projectEndDate)) {
        return 'Task Due Date cannot be after the Project End Date.';
      }
    }
    for (let i = 0; i < formData.subTasks.length; i++) {
      const st = formData.subTasks[i];
      if (!st.description.trim())
        return `Subtask #${i + 1}: description is required.`;
      if (!st.assignedToId)
        return `Subtask #${i + 1}: assignee is required.`;
      if (!st.dueDate)
        return `Subtask #${i + 1}: due date is required.`;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setLoadingSubmit(true);

    const taskPayload: TaskCreateDto = {
      description: formData.description.trim(),
      projectId: formData.projectId,
      assignedToId: formData.assignedToId,
      dueDate: formData.dueDate ?? null,
      status: formData.status,
      completionDate: formData.completionDate ?? null,
      priority: formData.priority,
      percentCompleted: formData.percentCompleted,
      remarks: formData.remarks || '',
      issues: formData.issues || '',
      subTasks: []
    };

    try {
      await createTask(taskPayload);
      toast.success('✅ Task created successfully');
      onTaskCreated();
      toggle();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create task');
    } finally {
      setLoadingSubmit(false);
    }
  };

  if (!isOpen || loadingMembers) return null;

  // ✅ Filter by same department
  const filteredTeamMembers = !department
    ? teamMembers // if department not found, show all
    : teamMembers.filter(member => member.department === department);

  return (
    <tr>
      <td style={{ width: '23%', padding: '8px' }}>
        <Input
          bsSize="sm"
          type="text"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Task Description"
          disabled={loadingSubmit}
          required
        />
      </td>


      <td style={{ width: '16%', padding: '8px' }}>
        <Input
          bsSize="sm"
          type="select"
          name="assignedToId"
          value={formData.assignedToId}
          onChange={handleChange}
          disabled={loadingSubmit}
          required
        >
          <option value="">-- Select Team Member --</option>
          {filteredTeamMembers.map(tm => (
            <option key={tm.userID} value={tm.userID}>{tm.name}</option>
          ))}
        </Input>
      </td>

      <td style={{ width: '17%', padding: '8px' }}>
        <Input
          type="select"
          name="status"
          value={formData.status}
          onChange={handleChange}
          required
        >
          <option value="">Select Status</option>
          {statusOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Input>
      </td>





      <td style={{ width: '14%', padding: '8px' }}>
        <Input
          bsSize="sm"
          type="date"
          name="dueDate"
          value={formData.dueDate ?? "" }
          onChange={handleChange}
          disabled={loadingSubmit || !projectEndDate}
          max={projectEndDate ? new Date(projectEndDate).toISOString().split('T')[0] : ''}
          required
        />
      </td>

      <td style={{ width: '12%', padding: '8px' }}>
        <Input
          bsSize="sm"
          type="select"
          name="priority"
          value={formData.priority}
          onChange={handleChange}
          disabled={loadingSubmit}
          required
        >
          {priorityOptions.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </Input>
      </td>



      <td style={{ width: '5%', padding: '8px' }}>
        <Input
          bsSize="sm"
          type="number"
          name="percentCompleted"
          value={formData.percentCompleted}
          onChange={handleChange}
          min={0}
          max={100}
          disabled={loadingSubmit}
          required
        />
      </td>

      <td></td>

      <td style={{ width: '5%', padding: '8px' }}>
        <Button
          type="submit"
          size="sm"
          color="primary"
          disabled={loadingSubmit || formData.projectId === 0}
          onClick={handleSubmit}
        >
          {loadingSubmit ? <Spinner size="sm" /> : 'Create'}
        </Button>
      </td>
    </tr>
  );
};

export default AddTaskSubTaskComponents;
